import { ingestionClient } from '@/shared/lib/api/ingestion-client'
import type {
  AiMessageRequest,
  AiSessionCount,
  AiSessionStatus,
  AiTurnResponse,
  PublishTemplateRequest,
  PublishTemplateResult,
  SaveDraftRequest,
  SaveDraftResult,
  TemplateAiDetail,
} from '../types/template-ai'

/**
 * Every AI-draft call of the editor, one function each (spec §10.1): a change of the backend contract is a change
 * of one line here.
 */
const TEMPLATES = '/parse-templates'

function templatePath(templateId: number, suffix: string): string {
  return `${TEMPLATES}/${templateId}${suffix}`
}

export function fetchTemplateAi(templateId: number): Promise<TemplateAiDetail> {
  return ingestionClient.get<TemplateAiDetail>(templatePath(templateId, '/ai'))
}

/** Saves the draft with no gate (D7/D18): 200 with the validation, even when it has errors. 409 on a stale revision. */
export function saveTemplateDraft(templateId: number, request: SaveDraftRequest): Promise<SaveDraftResult> {
  return ingestionClient.put<SaveDraftResult>(templatePath(templateId, '/draft'), request)
}

/** Draft → Active. A refusal keeps the draft saved and answers its revision and validation (`PublishRefusalBody`). */
export function publishTemplate(templateId: number, request: PublishTemplateRequest): Promise<PublishTemplateResult> {
  return ingestionClient.post<PublishTemplateResult>(templatePath(templateId, '/publish'), request)
}

export function discardTemplateDraft(templateId: number): Promise<void> {
  return ingestionClient.post<void>(templatePath(templateId, '/discard'))
}

export function sendTemplateAiMessage(templateId: number, request: AiMessageRequest): Promise<AiTurnResponse> {
  return ingestionClient.post<AiTurnResponse>(templatePath(templateId, '/ai/messages'), request)
}

/** Restores the draft from before the person's last chat turn; answers the whole AI layer after it. */
export function undoTemplateAiMessage(templateId: number, revision: number): Promise<TemplateAiDetail> {
  return ingestionClient.post<TemplateAiDetail>(templatePath(templateId, '/ai/messages/undo'), { revision })
}

/** A Failed session back to Pending; answers the whole AI layer after it. */
export function retryTemplateAi(templateId: number): Promise<TemplateAiDetail> {
  return ingestionClient.post<TemplateAiDetail>(templatePath(templateId, '/ai/retry'))
}

/** The menu pill and the «AI drafts» tab counter: how many of the tenant's sessions are in `status`. */
export function fetchAiSessionCount(status: AiSessionStatus): Promise<AiSessionCount> {
  return ingestionClient.get<AiSessionCount>(`${TEMPLATES}/ai-sessions/count?status=${encodeURIComponent(status)}`)
}

